// =============================================================================
// The cart: slide-in drawer, badge, and the two steppers.
//
// The mockup holds the basket in React state and re-renders. Here WooCommerce
// owns it, so every mutation is a round trip — but the response carries the
// drawer's HTML already rendered by the same PHP that printed the page, so
// there is never a second copy of that markup in JavaScript.
// =============================================================================

import { claimOverlay, releaseOverlay, onDismiss } from './overlay.js';

const settings = window.chengHua || {};

/**
 * Post to one of the cart endpoints.
 *
 * @param {string} action admin-ajax action name.
 * @param {Object} fields Extra POST fields.
 * @return {Promise<Object|null>} The success payload, or null if it failed.
 */
async function post(action, fields = {}) {
	if (!settings.ajaxUrl || !settings.cartNonce) {
		return null;
	}

	const body = new FormData();
	body.append('action', action);
	body.append('nonce', settings.cartNonce);

	Object.entries(fields).forEach(([key, value]) => body.append(key, value));

	try {
		const response = await fetch(settings.ajaxUrl, {
			method: 'POST',
			credentials: 'same-origin',
			body,
		});
		const payload = await response.json();

		return payload && payload.success ? payload.data : null;
	} catch {
		return null;
	}
}

export default function initCart() {
	const drawer = document.querySelector('[data-ch-drawer]');
	const openButton = document.querySelector('[data-ch-cart-open]');
	const badge = document.querySelector('[data-ch-cart-count]');
	const itemsRegion = document.querySelector('[data-ch-cart-items]');
	const totalsRegion = document.querySelector('[data-ch-cart-totals]');

	let lastFocused = null;

	function setDrawer(open) {
		if (!drawer) {
			return;
		}

		drawer.classList.toggle('is-open', open);
		drawer.setAttribute('aria-hidden', open ? 'false' : 'true');

		if (openButton) {
			openButton.setAttribute('aria-expanded', open ? 'true' : 'false');
		}

		if (open) {
			claimOverlay('cart');
			lastFocused = document.activeElement;
			drawer.querySelector('[data-ch-drawer-close]')?.focus();
		} else {
			releaseOverlay('cart');

			// Only pull focus back if it is still inside the drawer we just hid,
			// so a click elsewhere on the page is not yanked away.
			if (drawer.contains(document.activeElement) && lastFocused instanceof HTMLElement) {
				lastFocused.focus();
			}
		}
	}

	/**
	 * Restart the badge's cartPop animation.
	 *
	 * The rule applies unconditionally in _header.scss, so it plays once on load
	 * and never again. The mockup re-mounts the element by key to replay it;
	 * clearing the animation and forcing a reflow is the same trick without React.
	 */
	function bounceBadge() {
		if (!badge) {
			return;
		}

		badge.style.animation = 'none';
		void badge.offsetWidth;
		badge.style.animation = '';
	}

	/**
	 * Repaint every card so its stepper agrees with the cart.
	 *
	 * @param {Object} lines Cart lines keyed by product ID.
	 */
	function syncCards(lines) {
		document.querySelectorAll('[data-ch-product]').forEach((card) => {
			const line = lines[card.dataset.chProduct];
			const qty = card.querySelector('[data-ch-product-qty]');
			const num = card.querySelector('[data-ch-product-num]');
			const add = card.querySelector('.ch-product__add');

			if (!qty || !add) {
				return;
			}

			if (line) {
				qty.dataset.chCartKey = line.key;

				if (num) {
					num.textContent = String(line.quantity);
				}
			} else {
				qty.dataset.chCartKey = '';
			}

			qty.hidden = !line;
			add.hidden = Boolean(line);
		});
	}

	/**
	 * Apply a cart response to the badge, the drawer and the cards.
	 *
	 * @param {Object|null} data     Endpoint payload.
	 * @param {boolean}     bounce   Replay the badge animation.
	 */
	function apply(data, bounce = false) {
		if (!data) {
			return;
		}

		if (badge) {
			badge.textContent = String(data.count);
			badge.hidden = data.count === 0;

			if (bounce && data.count > 0) {
				bounceBadge();
			}
		}

		if (itemsRegion) {
			itemsRegion.innerHTML = data.itemsHtml;
		}

		if (totalsRegion) {
			totalsRegion.innerHTML = data.totalsHtml;
		}

		syncCards(data.lines || {});
	}

	// --- Opening and closing -------------------------------------------------

	if (openButton) {
		openButton.addEventListener('click', (event) => {
			// The button is a real link to the cart page for the no-script case;
			// with the drawer available that navigation is unwanted.
			event.preventDefault();
			setDrawer(!drawer?.classList.contains('is-open'));
		});
	}

	drawer?.querySelector('[data-ch-drawer-close]')?.addEventListener('click', () => setDrawer(false));

	onDismiss(() => setDrawer(false));

	// --- Card actions --------------------------------------------------------

	document.addEventListener('click', async (event) => {
		if (!(event.target instanceof Element)) {
			return;
		}

		const addButton = event.target.closest('[data-ch-product-add]');

		if (addButton) {
			// Only marked on products the endpoint can add directly; anything else
			// keeps its link through to the product page.
			event.preventDefault();

			const card = addButton.closest('[data-ch-product]');
			const data = await post('ch_cart_add', {
				product_id: card?.dataset.chProduct || 0,
				quantity: 1,
			});

			apply(data, true);

			if (data) {
				setDrawer(true);
			}

			return;
		}

		const step = event.target.closest('[data-ch-product-step]');

		if (step) {
			const qty = step.closest('[data-ch-product-qty]');
			const num = qty?.querySelector('[data-ch-product-num]');
			const key = qty?.dataset.chCartKey;

			if (!key) {
				return;
			}

			const current = parseInt(num?.textContent || '0', 10);
			const next = step.dataset.chProductStep === 'inc' ? current + 1 : current - 1;

			// A quantity of 0 removes the line, which is how the mockup's "−"
			// behaves at one.
			apply(await post('ch_cart_set_qty', { key, quantity: Math.max(0, next) }));

			return;
		}

		// --- Drawer line actions ---------------------------------------------

		const action = event.target.closest('[data-ch-cart-action]');

		if (!action) {
			return;
		}

		const item = action.closest('[data-ch-cart-key]');
		const key = item?.dataset.chCartKey;

		if (!key) {
			return;
		}

		const current = parseInt(item.querySelector('.ch-cart-item__num')?.textContent || '0', 10);
		let quantity = 0;

		if (action.dataset.chCartAction === 'inc') {
			quantity = current + 1;
		} else if (action.dataset.chCartAction === 'dec') {
			quantity = current - 1;
		}

		apply(await post('ch_cart_set_qty', { key, quantity: Math.max(0, quantity) }));
	});
}
